import { useState, useRef } from "react";
import { Upload, FileUp, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { adminApi } from "@/lib/api";
import { toast } from "sonner";

export default function UploadTab() {
  const [file, setFile] = useState(null);
  const [overwrite, setOverwrite] = useState(false);
  const [source, setSource] = useState("web_indexing");
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);

  const handleUpload = async () => {
    if (!file) { toast.error("Please select a file"); return; }
    setUploading(true);
    setResult(null);
    try {
      const res = await adminApi.uploadData(file, overwrite, source);
      setResult(res.data);
      toast.success(`Uploaded ${res.data.records_inserted} records across ${res.data.dates_uploaded} dates`);
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    }
    setUploading(false);
  };

  return (
    <div className="space-y-6" data-testid="upload-tab">
      {/* Drop Zone */}
      <div
        className="border-2 border-dashed border-slate-200 rounded-xl p-8 text-center cursor-pointer hover:border-blue-300 transition-colors"
        onClick={() => fileRef.current?.click()}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); setFile(e.dataTransfer.files[0]); }}
        data-testid="upload-dropzone"
      >
        <Upload size={32} className="mx-auto text-slate-300 mb-3" />
        <p className="text-sm text-slate-600">{file ? file.name : "Drop your file here, or click to browse"}</p>
        <p className="text-xs text-slate-400 mt-1">Supports .json, .csv formats</p>
        <input ref={fileRef} type="file" className="hidden" accept=".json,.csv,.txt" onChange={(e) => setFile(e.target.files[0])} data-testid="upload-file-input" />
      </div>

      {/* Options */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <Label className="text-sm font-medium text-slate-700 mb-2 block">Revenue Source</Label>
          <Select value={source} onValueChange={setSource}>
            <SelectTrigger className="h-10 border-slate-200" data-testid="upload-source-selector">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="web_indexing">Web-indexing</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-end">
          <label className="flex items-center gap-2 cursor-pointer" data-testid="upload-overwrite-checkbox">
            <Checkbox checked={overwrite} onCheckedChange={setOverwrite} />
            <span className="text-sm text-slate-700">Overwrite existing data</span>
          </label>
        </div>
      </div>

      {/* Format Help */}
      <div className="bg-slate-50 border border-slate-200 rounded-xl p-4" data-testid="upload-format-help">
        <p className="text-xs font-medium text-slate-600 mb-2">Supported Formats</p>
        <div className="grid grid-cols-2 gap-4 text-xs text-slate-500">
          <div>
            <p className="font-medium text-slate-700 mb-1">JSON</p>
            <code className="text-[10px] bg-white border border-slate-200 rounded px-2 py-1 block">[{`{"ymd":"2026-01-01","records":[{"campaign":"x","earnings":10,"peers":100}]}`}]</code>
          </div>
          <div>
            <p className="font-medium text-slate-700 mb-1">CSV</p>
            <code className="text-[10px] bg-white border border-slate-200 rounded px-2 py-1 block">ymd,campaign,earnings,peers<br/>2026-01-01,campaign_x,10.5,100</code>
          </div>
        </div>
      </div>

      {/* Upload Button */}
      <Button onClick={handleUpload} disabled={!file || uploading} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="upload-submit-btn">
        <FileUp size={16} className="mr-2" /> {uploading ? "Uploading..." : "Upload Data"}
      </Button>

      {/* Result */}
      {result && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-start gap-3" data-testid="upload-result">
          <CheckCircle2 size={20} className="text-emerald-600 mt-0.5 shrink-0" />
          <div className="text-sm text-emerald-800">
            <p className="font-medium">Upload Complete</p>
            <p className="text-xs mt-1">{result.records_inserted} records inserted across {result.dates_uploaded} dates. {result.dates_skipped > 0 && `${result.dates_skipped} dates skipped (already exist).`}</p>
          </div>
        </div>
      )}
    </div>
  );
}
